"use client";

import { useDevMode, useIsPWA } from "@/contexts/dev-mode-context";
import { Monitor, Smartphone } from "lucide-react";

/**
 * Floating toggle for switching between phone mockup and responsive modes.
 * Only visible on large screens (desktop) when not in PWA mode.
 */
export function DevModeToggle() {
	const { isPhoneMockupMode, setPhoneMockupMode, isLoaded } = useDevMode();
	const isPWA = useIsPWA();
	const isToolbarEnabled = process.env.NODE_ENV === "development"
		|| process.env.NEXT_PUBLIC_ENABLE_DESIGN_TOOLBAR === "true";

	// Production stays free of design controls unless an explicit public flag enables them.
	if (!isToolbarEnabled || !isLoaded || isPWA) {
		return null;
	}

	return (
		<div aria-label="Design preview" className="fixed bottom-4 right-4 z-[9999] hidden lg:flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-gray-200 p-1" role="group">
			<button
				aria-pressed={isPhoneMockupMode}
				onClick={() => setPhoneMockupMode(true)}
				className={`
					flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all
					${isPhoneMockupMode 
						? "bg-[var(--vp-primary)] text-white" 
						: "text-gray-600 hover:bg-gray-100"
					}
				`}
				title="Phone mockup mode (for design)"
				type="button"
			>
				<Smartphone className="w-4 h-4" />
				<span className="hidden xl:inline">Mockup</span>
			</button>
			<button
				aria-pressed={!isPhoneMockupMode}
				onClick={() => setPhoneMockupMode(false)}
				className={`
					flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all
					${!isPhoneMockupMode 
						? "bg-[var(--vp-primary)] text-white" 
						: "text-gray-600 hover:bg-gray-100"
					}
				`}
				title="Responsive mode (PWA preview)"
				type="button"
			>
				<Monitor className="w-4 h-4" />
				<span className="hidden xl:inline">Responsive</span>
			</button>
		</div>
	);
}
